import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect } from "react";
import Modal, { CommonModalProps } from "..";
import OutlinedCheckbox from "../../Forms/OutlinedCheckbox";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { setupService, userService } from "../../../Core/Api/services";
import { GroupAdvancedInfos } from "../../../Core/Api/Types/group-advanced-infos";

import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbUserGroupRoleInfos } from "../../../Core/Api/Types/db-user-group-role-infos";
import { CreateProviderSetupFullRequest } from "../../../Core/Api/Types/create-provider-setup-request";
import { produce } from "immer";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalAddProviderElementProps = {
  onClose?: (props: CloseClbProps) => void;
};
export type ModalAddProviderProps = CommonModalProps<ModalAddProviderElementProps>;

const ModalAddProvider = React.forwardRef<any, ModalAddProviderProps>((props, ref) => {
  const { closeModal, onClose, ...modalProps } = props;
  const { active: outlinedCheckboxChecked, toggle: toggleOutlinedCheckbox } = useToggle(false);
  const [loadingSubmit, setLoadingSubmit] = React.useState(false);
  const [groups, setGroups] = React.useState<GroupAdvancedInfos[]>([]);
  const [roles, setRoles] = React.useState<DbUserGroupRoleInfos[]>([]);
  const [providerRequest, setProviderRequest] = React.useState<CreateProviderSetupFullRequest>(
    new CreateProviderSetupFullRequest({
      disableAffinityCheck: false,
      copyPermsFromGroupId: null,
      copyPermsFromRoleId: null,
      firstName: "",
      labEmail: "",
      labName: "",
      lastName: "",
    })
  );
  const customSnackbar = useCustomSnackbar();
  const { t } = useTranslation();

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const handleConfirmClick = useCallback(() => {
    setLoadingSubmit(true);

    setupService.CreateProviderFull(providerRequest).then((response) => {
      setLoadingSubmit(false);

      if (response.success) {
        customSnackbar(t("alert:success.providerCreated"), "success");
        onClose && onClose({ confirm: true });
        closeModal();
      } else customSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
    });
  }, [
    providerRequest,
    providerRequest.labName,
    providerRequest.labEmail,
    providerRequest.firstName,
    providerRequest.lastName,
    providerRequest.copyPermsFromGroupId,
    providerRequest.copyPermsFromRoleId,
    providerRequest.disableAffinityCheck,
  ]);

  useEffect(() => {
    userService.GetGroupsList().then((response) => {
      if (response && response.groups) setGroups(response.groups);
    });
    userService.GetGroupRolesList().then((response) => {
      if (response && response.groupRoles) setRoles(response.groupRoles);
    });
  }, []);

  return (
    <Modal
      {...modalProps}
      title={t("common:newProvider")}
      reference={ref}
      closeModal={handleCancelClick}
      width={680}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleConfirmClick}
            disabled={
              providerRequest.labName === null ||
              providerRequest.labName?.length === 0 ||
              providerRequest.labEmail === null ||
              providerRequest.labEmail?.length === 0
            }
            loading={loadingSubmit}
          >
            {t("common:create")}
          </Button>
        </Stack>
      }
    >
      <>
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
            {t("common:provider")}
          </Typography>
          <Typography color="text.tier" mb={3}>
            {t("modal:modalAddProvider.providerHelpText")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="name">
                  {t("common:name")}
                </InputLabel>
                <OutlinedInput
                  id="name"
                  value={providerRequest.labName}
                  onChange={(e) => {
                    setProviderRequest(
                      produce((draft) => {
                        if (e.currentTarget?.value !== undefined) draft.labName = e.currentTarget?.value;
                        draft = { ...draft };
                      })
                    );
                  }}
                  placeholder={t("common:providerName")}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="email">
                  {t("common:email")}
                </InputLabel>
                <OutlinedInput
                  id="email"
                  value={providerRequest.labEmail}
                  onChange={(e) =>
                    setProviderRequest(
                      produce((draft) => {
                        draft.labEmail = e.currentTarget?.value;
                      })
                    )
                  }
                  placeholder={t("common:contactEmail")}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="name">
                  {t("common:firstName")}
                </InputLabel>
                <OutlinedInput
                  id="name"
                  value={providerRequest.firstName}
                  onChange={(e) =>
                    setProviderRequest(
                      produce((draft) => {
                        draft.firstName = e.currentTarget?.value;
                      })
                    )
                  }
                  placeholder={t("field:placeholder.firstNameOptional")}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="email">
                  {t("common:lastName")}
                </InputLabel>
                <OutlinedInput
                  id="name"
                  value={providerRequest.lastName}
                  onChange={(e) =>
                    setProviderRequest(
                      produce((draft) => {
                        draft.lastName = e.currentTarget?.value;
                      })
                    )
                  }
                  placeholder={t("field:placeholder.lastNameOptional")}
                />
              </FormControl>
            </Grid>
          </Grid>
          {UserHasAcl(AclTypes.aclSuperUser) && (
            <OutlinedCheckbox
              mt={2.5}
              checked={outlinedCheckboxChecked}
              onClick={() => {
                toggleOutlinedCheckbox();
                setProviderRequest(
                  produce((draft) => {
                    draft.disableAffinityCheck = !outlinedCheckboxChecked;
                  })
                );
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                {t("common:disableAffinityCheck")}
              </Typography>
            </OutlinedCheckbox>
          )}
        </Box>
        <Divider sx={{ mt: 3, mb: 3 }} />
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
            {t("modal:body.groupPermissions")}
          </Typography>
          <Typography color="text.tier" mb={3}>
            {t("modal:modalAddProvider.groupPermissionsHelpText")}
          </Typography>

          <Grid container spacing={2} mt={1}>
            <Grid item md={6}>
              <Autocomplete
                sx={{ mt: -2 }}
                options={groups}
                onChange={(e, v) => {
                  setProviderRequest(
                    produce((draft) => {
                      draft.copyPermsFromGroupId = v?.id ?? null;
                    })
                  );
                }}
                disabled={providerRequest.copyPermsFromRoleId !== null}
                getOptionLabel={(value: any) => value.group.name}
                isOptionEqualToValue={(option, value) => option.group!.id === value.group!.id}
                value={groups.find((x) => x.id === providerRequest.copyPermsFromGroupId)}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t("field:placeholder.selectPermissionGroup")} />
                )}
              />
            </Grid>
            <Grid item md={6}>
              <Autocomplete
                sx={{ mt: -2 }}
                options={roles}
                onChange={(e, v) => {
                  setProviderRequest(
                    produce((draft) => {
                      draft.copyPermsFromRoleId = v?.id ?? null;
                    })
                  );
                }}
                disabled={providerRequest.copyPermsFromGroupId !== null}
                getOptionLabel={(value: any) => value.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={roles.find((x) => x.id === providerRequest.copyPermsFromRoleId)}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t("field:placeholder.selectPermissionRole")} />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </>
    </Modal>
  );
});

export default ModalAddProvider;
